// src/pages/EventConnectInvestor.tsx
// Investor preference-submission page for the EventConnect conference platform.
// Route: /event-connect/preferences/:eventId
//
// Investors (allocators) pick and rank their top presenting managers for an event
// while its status is `investor_preferences`. They can add up to
// (investor_preference_limit + investor_preference_buffer) managers, reorder them
// by drag-and-drop (native HTML5, mirroring pulseUI's RankingInput), and submit.
// Colleagues' picks from the same firm are shown read-only for reference so a firm
// can spread its coverage.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { C, EventRow, fmtDateRange, aumLabel } from '../lib/event';
import {
  PageHeader, Banner, LoadingScreen, EmptyState, card, btnPrimary, btnGhost, SectionLabel,
} from '../lib/eventUI';

// ── Local view models ────────────────────────────────────────────────────────
interface ManagerCard {
  firmId: string;
  name: string;
  type: string;
  assetClass: string | null;
  strategy: string | null;
  aum: string; // pre-computed AUM label
}
interface ColleaguePicks {
  name: string;
  managers: string[]; // manager firm names, in the colleague's rank order
}

export default function EventConnectInvestor() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { firm } = useProfile();

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<EventRow | null>(null);
  const [managers, setManagers] = useState<ManagerCard[]>([]);
  const [prefs, setPrefs] = useState<string[]>([]);          // chosen manager firmIds, ranked
  const [colleagues, setColleagues] = useState<ColleaguePicks[]>([]);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fast lookup: firmId → manager card (used to render the right-hand ranked list).
  const managerById = useMemo(() => {
    const m: Record<string, ManagerCard> = {};
    managers.forEach(mg => { m[mg.firmId] = mg; });
    return m;
  }, [managers]);

  const limit = event?.investor_preference_limit ?? 0;
  const buffer = event?.investor_preference_buffer ?? 0;
  const maxPrefs = limit + buffer;
  const atMax = prefs.length >= maxPrefs && maxPrefs > 0;

  // ── Load event, presenting managers and any existing preferences ──────────────
  useEffect(() => {
    if (!user || !eventId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        // 1) The event itself.
        const { data: ev } = await supabase
          .from('events').select('*').eq('id', eventId).single();
        if (cancelled) return;
        const eventRow = (ev as EventRow) || null;
        setEvent(eventRow);

        // Only bother loading the rest when preferences are actually open.
        if (!eventRow || eventRow.status !== 'investor_preferences') {
          if (!cancelled) setLoading(false);
          return;
        }

        // 2) Presenting managers for this event (selected/confirmed registrations),
        //    joining the firm row (+ AUM fields so aumLabel can render a range).
        const { data: regs } = await supabase
          .from('event_registrations')
          .select('firm_id, firm:firms(id, name, type, aum_min, aum_max, aum_exact)')
          .eq('event_id', eventId)
          .eq('role_at_event', 'presenting_manager')
          .in('status', ['selected', 'confirmed']);

        // A firm can have several registered attendees, collapse to unique firms.
        const firmMap: Record<string, { name: string; type: string; aum: string }> = {};
        (regs || []).forEach((r: any) => {
          const f = Array.isArray(r.firm) ? r.firm[0] : r.firm;
          const fid = f?.id || r.firm_id;
          if (!fid || firmMap[fid]) return;
          firmMap[fid] = {
            name: f?.name || 'Unknown firm',
            type: f?.type || '',
            aum: aumLabel(f),
          };
        });
        const firmIds = Object.keys(firmMap);

        // 3) A representative fund per manager firm for asset class / strategy.
        const repByFirm: Record<string, { assetClass: string | null; strategy: string | null }> = {};
        if (firmIds.length) {
          const { data: funds } = await supabase
            .from('funds')
            .select('firm_id, asset_class, strategy')
            .in('firm_id', firmIds);
          (funds || []).forEach((fd: any) => {
            if (fd.firm_id && !repByFirm[fd.firm_id]) {
              repByFirm[fd.firm_id] = { assetClass: fd.asset_class ?? null, strategy: fd.strategy ?? null };
            }
          });
        }

        const cards: ManagerCard[] = firmIds
          .map(fid => ({
            firmId: fid,
            name: firmMap[fid].name,
            type: firmMap[fid].type,
            assetClass: repByFirm[fid]?.assetClass ?? null,
            strategy: repByFirm[fid]?.strategy ?? null,
            aum: firmMap[fid].aum,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        if (cancelled) return;
        setManagers(cards);

        // 4) Pre-populate the right panel from this investor's existing preferences.
        const { data: existing } = await supabase
          .from('event_investor_preferences')
          .select('manager_firm_id, preference_rank')
          .eq('event_id', eventId)
          .eq('profile_id', user.id)
          .order('preference_rank', { ascending: true });
        if (cancelled) return;
        const mine = (existing || [])
          .map((p: any) => p.manager_firm_id as string)
          .filter((id: string | null): id is string => !!id);
        setPrefs(mine);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load event.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, eventId]);

  // ── Load colleagues' preferences (needs the investor's firm, which resolves
  //    asynchronously via useProfile), kept separate so it never clobbers edits. ─
  useEffect(() => {
    if (!user || !eventId || !firm?.id) return;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await supabase
          .from('event_investor_preferences')
          .select('profile_id, preference_rank, profile:profiles(full_name), manager_firm:firms(name)')
          .eq('event_id', eventId)
          .eq('firm_id', firm.id)
          .neq('profile_id', user.id)
          .order('preference_rank', { ascending: true });
        if (cancelled) return;

        // Group rows by colleague, preserving rank order for their picks.
        const grouped: Record<string, ColleaguePicks> = {};
        (data || []).forEach((row: any) => {
          const prof = Array.isArray(row.profile) ? row.profile[0] : row.profile;
          const mf = Array.isArray(row.manager_firm) ? row.manager_firm[0] : row.manager_firm;
          const key = row.profile_id as string;
          if (!key) return;
          if (!grouped[key]) grouped[key] = { name: prof?.full_name || 'Colleague', managers: [] };
          if (mf?.name) grouped[key].managers.push(mf.name);
        });
        setColleagues(Object.values(grouped));
      } catch {
        if (!cancelled) setColleagues([]);
      }
    })();

    return () => { cancelled = true; };
  }, [user, eventId, firm?.id]);

  // ── Preference list mutations ─────────────────────────────────────────────────
  const addPref = (firmId: string) => {
    setSubmitted(false);
    setPrefs(prev => (prev.includes(firmId) || prev.length >= maxPrefs ? prev : [...prev, firmId]));
  };
  const removePref = (firmId: string) => {
    setSubmitted(false);
    setPrefs(prev => prev.filter(id => id !== firmId));
  };
  // Reorder within the ranked list (drag-drop and typed-rank both route here).
  const move = (from: number, to: number) => {
    if (to < 0 || to >= prefs.length || from === to) return;
    setSubmitted(false);
    setPrefs(prev => {
      const next = [...prev];
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next;
    });
  };

  // ── Submit: replace this investor's preference rows with the current order ─────
  const submit = async () => {
    if (!user || !eventId || !prefs.length) return;
    setSubmitting(true);
    setError(null);
    try {
      await supabase
        .from('event_investor_preferences')
        .delete()
        .eq('event_id', eventId)
        .eq('profile_id', user.id);

      const rows = prefs.map((firmId, i) => ({
        event_id: eventId,
        profile_id: user.id,
        firm_id: firm?.id ?? null,
        manager_firm_id: firmId,
        preference_rank: i + 1,
      }));
      const { error: insErr } = await supabase.from('event_investor_preferences').insert(rows);
      if (insErr) throw insErr;
      setSubmitted(true);
    } catch (e: any) {
      setError(e?.message || 'Could not submit your preferences. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const back = () => navigate('/event-connect');
  const pageWrap: React.CSSProperties = {
    padding: '28px 32px', minHeight: '100vh', background: C.surface,
    fontFamily: 'system-ui, -apple-system, sans-serif',
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  if (loading) return <LoadingScreen />;

  // Event missing entirely.
  if (!event) {
    return (
      <div style={pageWrap}>
        <button onClick={back} style={{ ...btnGhost, marginBottom: '16px' }}>← Back to EventConnect</button>
        <Banner type="info" text="This event could not be found." />
      </div>
    );
  }

  // Preferences not open, informational stop.
  if (event.status !== 'investor_preferences') {
    return (
      <div style={pageWrap}>
        <button onClick={back} style={{ ...btnGhost, marginBottom: '16px' }}>← Back to EventConnect</button>
        <Banner type="info" text="Preference submission is not currently open for this event." />
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <PageHeader
        title={event.name}
        subtitle={`${fmtDateRange(event.start_date, event.end_date)}${event.location ? ` · ${event.location}` : ''}`}
        right={<button onClick={back} style={btnGhost}>← Back to EventConnect</button>}
      />

      <Banner
        type="info"
        text={`Select your top ${limit} presenting managers. You can submit up to ${maxPrefs} preferences.`}
      />
      {error && <Banner type="err" text={error} />}
      {submitted && <Banner type="ok" text="Your preferences have been submitted." />}

      {/* Two-column layout: available managers (left) / ranked preferences (right). */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

        {/* ── LEFT: available presenting managers ── */}
        <div>
          <SectionLabel style={{ marginBottom: '10px' }}>Presenting Managers</SectionLabel>
          {managers.length === 0 ? (
            <EmptyState text="No presenting managers have been confirmed for this event yet." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {managers.map(mg => {
                const added = prefs.includes(mg.firmId);
                const disableAdd = added || atMax;
                const meta = [mg.assetClass, mg.strategy].filter(Boolean).join(' · ');
                return (
                  <div key={mg.firmId} style={{ ...card, padding: '15px 17px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: C.navy, marginBottom: '3px' }}>{mg.name}</div>
                        {meta && <div style={{ fontSize: '12px', color: C.muted, marginBottom: mg.aum ? '2px' : 0 }}>{meta}</div>}
                        {mg.aum && <div style={{ fontSize: '11.5px', color: C.textSec, fontWeight: 600 }}>{mg.aum} AUM</div>}
                      </div>
                      <button
                        onClick={() => addPref(mg.firmId)}
                        disabled={disableAdd}
                        style={{
                          ...btnGhost, flexShrink: 0, whiteSpace: 'nowrap',
                          cursor: disableAdd ? 'default' : 'pointer',
                          opacity: added ? 0.55 : atMax ? 0.5 : 1,
                          color: added ? C.success : C.textSec,
                          borderColor: added ? '#BBF7D0' : C.border,
                        }}
                      >
                        {added ? '✓ Added' : atMax ? 'Max reached' : 'Add to preferences'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── RIGHT: my ranked preferences + colleagues' picks ── */}
        <div>
          <SectionLabel style={{ marginBottom: '10px' }}>
            My Preferences {maxPrefs > 0 ? `(${prefs.length}/${maxPrefs})` : ''}
          </SectionLabel>

          {prefs.length === 0 ? (
            <EmptyState text="No preferences selected yet. Add managers from the left, then drag to rank them (1 = top choice)." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {prefs.map((firmId, i) => {
                const mg = managerById[firmId];
                return (
                  <div
                    key={firmId}
                    draggable
                    onDragStart={() => setDragIdx(i)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); if (dragIdx !== null) move(dragIdx, i); setDragIdx(null); }}
                    onDragEnd={() => setDragIdx(null)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 12px', borderRadius: '4px',
                      border: `1.5px solid ${dragIdx === i ? C.gold : C.border}`,
                      background: dragIdx === i ? 'rgba(201,168,76,0.06)' : C.white,
                      cursor: 'grab',
                    }}
                  >
                    {/* Rank number, editable to jump to a position, like RankingInput. */}
                    <input
                      type="number" min={1} max={prefs.length} value={i + 1}
                      onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) move(i, v - 1); }}
                      onClick={e => e.stopPropagation()}
                      onMouseDown={e => e.stopPropagation()}
                      style={{
                        width: '42px', height: '32px', textAlign: 'center', flexShrink: 0,
                        border: `1px solid ${C.border}`, borderRadius: '50%', padding: 0,
                        fontSize: '13px', fontWeight: 700, color: C.navy, fontFamily: 'Georgia, serif',
                        background: C.surface, outline: 'none',
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0, fontSize: '13.5px', color: C.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {mg?.name || 'Manager'}
                    </span>
                    <span style={{ color: C.mutedLt, fontSize: '15px', lineHeight: 1, cursor: 'grab' }} title="Drag to reorder">⋮⋮</span>
                    <button
                      onClick={() => removePref(firmId)}
                      style={{ background: 'none', border: 'none', color: C.mutedLt, fontSize: '18px', lineHeight: 1, cursor: 'pointer', padding: '0 2px', flexShrink: 0 }}
                      title="Remove"
                    >×</button>
                  </div>
                );
              })}
              <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>Drag to reorder, or type a rank number. 1 = top choice.</div>
            </div>
          )}

          <div style={{ marginTop: '16px' }}>
            <button onClick={submit} disabled={prefs.length === 0 || submitting} style={{ ...btnPrimary, width: '100%', opacity: prefs.length === 0 || submitting ? 0.55 : 1, cursor: prefs.length === 0 || submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Submitting…' : 'Submit Preferences'}
            </button>
          </div>

          {/* Colleagues' selections, read-only reference to avoid overlap. */}
          {colleagues.length > 0 && (
            <div style={{ marginTop: '22px' }}>
              <SectionLabel style={{ marginBottom: '10px' }}>Your firm's other selections (for reference)</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {colleagues.map((c, i) => (
                  <div key={i} style={{ ...card, padding: '13px 15px', background: '#FCFCFD' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: C.mutedLt, marginBottom: '6px' }}>{c.name}</div>
                    {c.managers.length ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {c.managers.map((m, j) => (
                          <span key={j} style={{ fontSize: '11.5px', color: C.mutedLt, background: C.surface, border: `1px solid ${C.borderLt}`, borderRadius: '3px', padding: '3px 8px' }}>{m}</span>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: '11.5px', color: C.mutedLt }}>No selections yet.</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
